var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { useTranslation } from "react-i18next";
import { useList } from "../CList/useList";
import { formatFullDateTime } from "../../utils/date.util";
import { LogsService } from "../../services/logs/logs.service";
import { ActionType, AuditAction, AuditOutcome } from "../../models/logs/logs.model";
import { useAllSectors } from "../../utils/hooks/sectors/useSectors";
import { useAllResources } from "../../utils/hooks/resources/useResources";
/**
 * Turns a camelCase / snake_case field key into a human readable label.
 * e.g. "firstName" -> "First name", "arrival_date" -> "Arrival date"
 */
var humanizeKey = function (key) {
    var spaced = key
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};
var isIsoDate = function (value) {
    return /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}.*)?$/.test(value);
};
var LogDetails = function (_a) {
    var userId = _a.userId, entityId = _a.entityId, entityName = _a.entityName, sectorId = _a.sectorId, listId = _a.listId;
    var t = useTranslation().t;
    var sectors = useAllSectors();
    var resources = useAllResources();
    var resourceNameById = React.useMemo(function () {
        var map = new Map();
        resources.forEach(function (r) {
            if (r.id && r.name)
                map.set(r.id, r.name);
        });
        return map;
    }, [resources]);
    var sectorName = function (id) {
        if (!id)
            return "-";
        var sector = sectors.find(function (s) { return s.id === id; });
        if (!sector)
            return "-";
        return sector.name || (sector.sectorType ? t(sector.sectorType) : "-");
    };
    /** Resolves a single resource id to its name, falling back to the id when unknown. */
    var resourceName = function (id) { return resourceNameById.get(id) || id; };
    /** Field keys that hold resource id(s) — e.g. "resourceId", "resourceIds", "rootResourceId". */
    var isResourceKey = function (key) {
        var k = key.toLowerCase();
        return k.includes("resource") && k.includes("id");
    };
    /** Formats a resource id field by showing resource names instead of raw ids. */
    var formatResourceValue = function (value) {
        if (value === null || value === undefined || value === "") {
            return t("empty");
        }
        if (Array.isArray(value)) {
            if (value.length === 0)
                return t("empty");
            return value
                .filter(function (v) { return v !== null && v !== undefined && v !== ""; })
                .map(function (v) { return resourceName(String(v)); })
                .join(", ");
        }
        return resourceName(String(value));
    };
    var formatValue = function (value) {
        if (value === null || value === undefined || value === "") {
            return t("empty");
        }
        if (typeof value === "boolean") {
            return value ? t("Yes") : t("No");
        }
        if (typeof value === "number") {
            return String(value);
        }
        if (typeof value === "string") {
            var v = value.trim();
            if (!v)
                return t("empty");
            if (isIsoDate(v)) {
                var parsed = new Date(v);
                if (!isNaN(parsed.getTime()))
                    return formatFullDateTime(parsed);
            }
            return v.length > 120 ? "".concat(v.slice(0, 120), "\u2026") : v;
        }
        if (Array.isArray(value)) {
            if (value.length === 0)
                return t("empty");
            var allPrimitive = value.every(function (v) { return v === null || typeof v !== "object"; });
            if (allPrimitive) {
                return value.map(function (v) { return formatValue(v); }).join(", ");
            }
            return t("{{count}} items", { count: value.length });
        }
        if (typeof value === "object") {
            var labelKeys = ["name", "title", "label", "code", "firstName", "email", "status", "type"];
            var found = labelKeys.find(function (k) { return value[k] !== undefined && value[k] !== null && value[k] !== ""; });
            if (found)
                return String(value[found]);
            var keys = Object.keys(value);
            return keys.length ? t("{{count}} fields", { count: keys.length }) : t("empty");
        }
        return String(value);
    };
    var safeParse = function (json) {
        if (!json)
            return {};
        try {
            var parsed = JSON.parse(json);
            return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
        }
        catch (_a) {
            return {};
        }
    };
    var buildChanges = function (item) {
        if (Array.isArray(item.changes)) {
            return item.changes.map(function (change) {
                var isResource = isResourceKey(change.path);
                var format = isResource ? formatResourceValue : formatValue;
                var redactedValue = t("Redacted");
                return {
                    label: isResource
                        ? humanizeKey(change.path).replace(/\s*\bids\b\s*$/i, "s").replace(/\s*\bid\b\s*$/i, "")
                        : humanizeKey(change.path),
                    oldValue: change.redacted ? redactedValue : format(change.before),
                    newValue: change.redacted ? redactedValue : format(change.after),
                    changed: true,
                    redacted: change.redacted
                };
            });
        }
        var oldObj = safeParse(item.oldValueJson);
        var newObj = safeParse(item.newValueJson);
        var ignored = new Set(["updateDate", "insertDate", "id", "_id", "_class"]);
        var keys = Array.from(new Set(__spreadArray(__spreadArray([], Object.keys(oldObj), true), Object.keys(newObj), true)))
            .filter(function (k) { return !ignored.has(k); });
        return keys.map(function (key) {
            var isResource = isResourceKey(key);
            var format = isResource ? formatResourceValue : formatValue;
            var oldValue = format(oldObj[key]);
            var newValue = format(newObj[key]);
            return {
                // Resource id fields display resource names, so drop the "id"/"ids"
                // suffix from the label (e.g. "Resource ids" -> "Resources").
                label: isResource
                    ? humanizeKey(key).replace(/\s*\bids\b\s*$/i, "s").replace(/\s*\bid\b\s*$/i, "")
                    : humanizeKey(key),
                oldValue: oldValue,
                newValue: newValue,
                changed: oldValue !== newValue
            };
        });
    };
    var baseColumns = [
        { name: t("Timestamp"), id: "insertDate", width: "xl" },
        { name: t("Action"), id: "actionType", width: "m" },
        { name: t("Sector"), id: "sectorId", width: "l" },
        { name: t("User"), id: "userFullName", width: "l" },
        { name: t("Changes"), id: "changes", width: "xxxl" }
    ];
    var formatUserFullName = function (fullName) {
        var sanitized = fullName === null || fullName === void 0 ? void 0 : fullName.split(/\s+/).filter(function (part) {
            var normalized = part.trim().toLowerCase();
            return !!normalized && normalized !== "null" && normalized !== "undefined";
        }).join(" ").trim();
        return sanitized || t("System");
    };
    var effectiveAction = function (item) { return item.action || item.actionType; };
    var timestamp = function (item) {
        if (item.occurredAt instanceof Date)
            return item.occurredAt;
        if (item.occurredAt) {
            var parsed = new Date(item.occurredAt);
            if (!isNaN(parsed.getTime()))
                return parsed;
        }
        return item.insertDate;
    };
    var actionLabel = function (action) {
        switch (action) {
            case ActionType.CREATE:
                return t("Created");
            case ActionType.UPDATE:
                return t("Updated");
            case ActionType.DELETE:
                return t("Deleted");
            default:
                return action ? humanizeKey(action) : "-";
        }
    };
    var actionColor = function (action, outcome) {
        if (outcome === AuditOutcome.FAILURE)
            return "error";
        if (outcome === AuditOutcome.PARTIAL)
            return "warning";
        switch (action) {
            case AuditAction.CREATE:
                return "success";
            case AuditAction.UPDATE:
            case AuditAction.ACCESS_CHANGE:
            case AuditAction.SEND:
            case AuditAction.CONFIGURE:
            case AuditAction.EXECUTE:
                return "info";
            case AuditAction.DELETE:
                return "error";
            case AuditAction.EXPORT:
            case AuditAction.IMPORT:
            case AuditAction.IMPERSONATE:
            case AuditAction.REFUND:
                return "warning";
            default:
                return "default";
        }
    };
    var renderChanges = function (item) {
        var changes = buildChanges(item).filter(function (c) { return c.changed; });
        var action = effectiveAction(item);
        if (changes.length === 0) {
            // No field-level diff stored (e.g. create / delete actions).
            if (action === ActionType.CREATE) {
                return _jsx(Typography, __assign({ variant: "body2" }, { children: t("Entry was created") }));
            }
            if (action === ActionType.DELETE) {
                return _jsx(Typography, __assign({ variant: "body2" }, { children: t("Entry was deleted") }));
            }
            return _jsx(Typography, __assign({ variant: "body2", color: "text.secondary" }, { children: t("No detailed changes recorded") }));
        }
        var isCreate = action === ActionType.CREATE;
        var isDelete = action === ActionType.DELETE;
        return (_jsx(Stack, __assign({ sx: { gap: 0.75, py: 0.5 } }, { children: changes.map(function (change, i) { return (_jsxs(Stack, __assign({ direction: "row", sx: { flexWrap: "wrap", alignItems: "center", gap: 0.75 } }, { children: [_jsxs(Typography, __assign({ variant: "body2", sx: { fontWeight: 600 } }, { children: [change.label, ":"] })), change.redacted ? (_jsx(Typography, __assign({ variant: "body2", color: "text.secondary" }, { children: t("Value changed (redacted)") }))) : !isCreate && (_jsx(Typography, __assign({ variant: "body2", sx: {
                            color: "error.main",
                            textDecoration: isDelete ? "none" : "line-through",
                            opacity: 0.85
                        } }, { children: change.oldValue }))), !change.redacted && !isCreate && !isDelete && (_jsx(ArrowRightAltIcon, { fontSize: "small", sx: { color: "text.disabled" } })), !change.redacted && !isDelete && (_jsx(Typography, __assign({ variant: "body2", sx: { color: "success.main", fontWeight: 500 } }, { children: change.newValue })))] }), i)); }) })));
    };
    var effectiveListId = listId || "LogList_".concat(userId || entityId || "all");
    var list = useList({
        list: function (page, size, search, sortProp, sortDir) {
            return LogsService.getUserLogs(__assign(__assign(__assign(__assign({ limit: {
                    offset: page * size,
                    limit: size,
                    sort: [{ field: sortProp, direction: sortDir }]
                } }, (userId ? { userId: userId } : {})), (entityId ? { entityId: entityId } : {})), (entityName ? { entityName: entityName } : {})), (sectorId ? { sectorId: sectorId } : {})));
        },
        listId: effectiveListId,
        searchable: true,
        columns: baseColumns,
        allColumns: baseColumns,
        renderCell: function (item, column) {
            var _a, _b, _c;
            switch (column === null || column === void 0 ? void 0 : column.id) {
                case "insertDate":
                    return formatFullDateTime(timestamp(item));
                case "actionType": {
                    var action = effectiveAction(item);
                    return (_jsx(Chip, { size: "small", label: actionLabel(action), color: actionColor(action, item.outcome), variant: "outlined" }));
                }
                case "sectorId":
                    return ((_a = item.sector) === null || _a === void 0 ? void 0 : _a.displayName) || sectorName(((_b = item.sector) === null || _b === void 0 ? void 0 : _b.id) || item.sectorId);
                case "userFullName":
                    return formatUserFullName(((_c = item.actor) === null || _c === void 0 ? void 0 : _c.displayName) || item.userFullName);
                case "changes":
                    return _jsx(Box, { children: renderChanges(item) });
                default:
                    return null;
            }
        },
        initialSortProp: "insertDate",
        initialSortDir: "DESC"
    }, userId, entityId, entityName, sectorId);
    return (_jsx(Stack, __assign({ sx: { gap: 2 } }, { children: list.renderList() })));
};
export default LogDetails;
